import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  onSnapshot,
  increment
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import { getAuth } from 'firebase/auth';
import { fetchPortalUsersLinkedToAccounts } from '../accountTeamMembersService';
import { fetchPublicProfile } from '../publicProfilesService';

const COMMUNITY_POSTS_COLLECTION = 'communityPosts';
const COMMUNITY_COMMENTS_COLLECTION = 'communityComments';
const USERS_COLLECTION = 'users';

const getCommunityDirectoryFn = httpsCallable(functions, 'getCommunityDirectory');

/**
 * Client companies in Community and the Messages picker (active Custom / Lite / Plus /
 * Pro clients, same rule as isMessagingCommunityClient). Clients may only read their own
 * account (firestore.rules), so the server returns just the directory fields:
 * { id, company, contact: { firstName, lastName, name, email }, email, communityStatus, createdAt }.
 */
export const fetchCommunityDirectory = async () => {
  const { data } = await getCommunityDirectoryFn();
  return (data?.accounts || []).map((a) => ({ ...a, createdAt: a.createdAt ? new Date(a.createdAt) : null }));
};

function contactFallbackMemberFields(account) {
  const contact = account.contact || {};
  const firstName = contact.firstName || contact.name?.split(' ')[0] || 'Client';
  const lastName = contact.lastName || contact.name?.split(' ').slice(1).join(' ') || '';
  const displayName = contact.name || `${firstName} ${lastName}`.trim();
  const email = contact.email || '';
  return { firstName, lastName, displayName, email };
}

/** Directory accounts (fetchCommunityDirectory) → primary row + extra portal team logins (My Profile photos only). */
async function buildCommunityMemberRows(directoryAccounts) {
  const plain = [...directoryAccounts];

  plain.sort((a, b) => {
    const dateA = a.createdAt?.toDate?.() ?? a.createdAt;
    const dateB = b.createdAt?.toDate?.() ?? b.createdAt;
    const tA = dateA?.toDate ? dateA.toDate() : dateA instanceof Date ? dateA : new Date(0);
    const tB = dateB?.toDate ? dateB.toDate() : dateB instanceof Date ? dateB : new Date(0);
    return tB - tA;
  });

  const linkedMap = await fetchPortalUsersLinkedToAccounts(plain);
  const rows = [];

  for (const account of plain) {
    const linked = linkedMap.get(account.id) || [];
    const primary = linked.find((u) => u.isPrimary);
    const additional = linked.filter((u) => !u.isPrimary);
    const fb = contactFallbackMemberFields(account);
    const company = account.company || 'No company';
    const createdAt = account.createdAt?.toDate?.() ?? account.createdAt;

    if (primary) {
      rows.push({
        id: account.id,
        firstName: primary.firstName || fb.firstName,
        lastName: primary.lastName || fb.lastName,
        displayName: primary.name || fb.displayName,
        email: primary.email || fb.email,
        company,
        photoURL: primary.profileImage || null,
        accountId: account.id,
        communityStatus: account.communityStatus,
        createdAt,
      });
    } else {
      rows.push({
        id: account.id,
        firstName: fb.firstName,
        lastName: fb.lastName,
        displayName: fb.displayName,
        email: fb.email,
        company,
        photoURL: null,
        accountId: account.id,
        communityStatus: account.communityStatus,
        createdAt,
      });
    }

    for (const u of additional) {
      rows.push({
        id: u.uid,
        firstName: u.firstName,
        lastName: u.lastName,
        displayName: u.name,
        email: u.email,
        company,
        photoURL: u.profileImage || null,
        accountId: account.id,
        communityStatus: account.communityStatus,
        createdAt,
      });
    }
  }

  return rows;
}

// Helper function to get user data for posts and comments
export const getUserDataForPost = async (userId) => {
  try {
    console.log('Getting user data for userId:', userId);
    
    // Check if user is in internal_users collection
    const internalUserDoc = await getDoc(doc(db, 'internal_users', userId));
    if (internalUserDoc.exists()) {
      const internalData = internalUserDoc.data();
      console.log('Found internal user data:', internalData);
      // Build author name from the most complete fields we can find
      const firstName =
        internalData.firstName ||
        internalData.first_name ||
        '';
      const lastName =
        internalData.lastName ||
        internalData.last_name ||
        '';
      const combinedName = `${firstName} ${lastName}`.trim();
      const authorName =
        internalData.fullName ||
        internalData.full_name ||
        combinedName ||
        internalData.displayName ||
        internalData.name ||
        (internalData.email ? internalData.email.split('@')[0] : '') ||
        'Internal User';
      return {
        authorName,
        authorAvatar: internalData.profileImage || internalData.photoURL || internalData.profilePicture || null,
        userType: 'internal'
      };
    }

    // Portal author: their users doc may belong to another company (firestore.rules),
    // so the name and photo come from their public profile.
    const profile = await fetchPublicProfile(userId);
    if (profile) {
      const userData = {
        displayName: profile.name,
        firstName: profile.firstName,
        lastName: profile.lastName,
        profileImage: profile.profileImage,
      };

      // For portal users, prioritize their profile data from the users collection
      const firstName =
        userData.firstName ||
        userData.first_name ||
        '';
      const lastName =
        userData.lastName ||
        userData.last_name ||
        '';
      const combinedName = `${firstName} ${lastName}`.trim();
      const authorName =
        userData.displayName ||
        userData.fullName ||
        userData.full_name ||
        combinedName ||
        (userData.email ? userData.email.split('@')[0] : '') ||
        'Client';
      const authorAvatar = userData.profileImage || userData.photoURL || userData.profilePicture || null;
      
      console.log('Using user profile data:', { authorName, authorAvatar, firstName, lastName });
      return {
        authorName,
        authorAvatar,
        userType: 'client'
      };
    }

    console.log('No user data found, using fallback');
    // Fallback to default values
    return {
      authorName: 'User',
      authorAvatar: null,
      userType: 'internal'
    };
  } catch (error) {
    console.warn('Error fetching user data:', error);
    return {
      authorName: 'User',
      authorAvatar: null,
      userType: 'internal'
    };
  }
};

// Get all community posts
export const getAllCommunityPosts = async () => {
  try {
    const postsRef = collection(db, COMMUNITY_POSTS_COLLECTION);
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    // Process posts and populate author data
    const posts = await Promise.all(
      querySnapshot.docs.map(async (doc) => {
        const postData = doc.data();
        
        // Get current author data
        const userData = await getUserDataForPost(postData.authorId);
        
        // Debug logging
        console.log('Post author data for post', doc.id, ':', {
          authorId: postData.authorId,
          userData,
          originalAuthor: postData.author,
          originalAvatar: postData.authorAvatar
        });
        
        return {
          id: doc.id,
          ...postData,
          author: userData.authorName,
          authorAvatar: userData.authorAvatar,
          userType: userData.userType,
          comments: postData.comments || 0,
          likes: postData.likes || 0,
          likedBy: postData.likedBy || [],
          createdAt: postData.createdAt?.toDate?.() || postData.createdAt,
          updatedAt: postData.updatedAt?.toDate?.() || postData.updatedAt
        };
      })
    );
    
    return posts;
  } catch (error) {
    console.error('Error getting community posts:', error);
    throw error;
  }
};

// Get recent community posts (for dashboard)
export const getRecentCommunityPosts = async (limit = 3) => {
  try {
    const postsRef = collection(db, COMMUNITY_POSTS_COLLECTION);
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    // Process posts and populate author data
    const posts = await Promise.all(
      querySnapshot.docs.slice(0, limit).map(async (doc) => {
        const postData = doc.data();
        
        // Get current author data
        const userData = await getUserDataForPost(postData.authorId);
        
        return {
          id: doc.id,
          ...postData,
          author: userData.authorName,
          authorAvatar: userData.authorAvatar,
          userType: userData.userType,
          comments: postData.comments || 0,
          likes: postData.likes || 0,
          likedBy: postData.likedBy || [],
          createdAt: postData.createdAt?.toDate?.() || postData.createdAt,
          updatedAt: postData.updatedAt?.toDate?.() || postData.updatedAt
        };
      })
    );
    
    return posts;
  } catch (error) {
    console.error('Error getting recent community posts:', error);
    throw error;
  }
};

// Get posts by type
export const getPostsByType = async (type) => {
  try {
    const postsRef = collection(db, COMMUNITY_POSTS_COLLECTION);
    const q = query(
      postsRef, 
      where('type', '==', type),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      comments: doc.data().comments || 0, // Ensure comments field exists
      likes: doc.data().likes || 0, // Ensure likes field exists
      likedBy: doc.data().likedBy || [], // Ensure likedBy field exists
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
    }));
  } catch (error) {
    console.error('Error getting posts by type:', error);
    throw error;
  }
};

// Get posts by category
export const getPostsByCategory = async (category) => {
  try {
    const postsRef = collection(db, COMMUNITY_POSTS_COLLECTION);
    const q = query(
      postsRef, 
      where('category', '==', category),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      comments: doc.data().comments || 0, // Ensure comments field exists
      likes: doc.data().likes || 0, // Ensure likes field exists
      likedBy: doc.data().likedBy || [], // Ensure likedBy field exists
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
    }));
  } catch (error) {
    console.error('Error getting posts by category:', error);
    throw error;
  }
};

// Get a single post by ID
export const getPostById = async (postId) => {
  try {
    const postRef = doc(db, COMMUNITY_POSTS_COLLECTION, postId);
    const postDoc = await getDoc(postRef);
    
    if (postDoc.exists()) {
      const postData = postDoc.data();
      
      // Get comments for this post
      const commentsRef = collection(db, COMMUNITY_COMMENTS_COLLECTION);
      const commentsQuery = query(
        commentsRef, 
        where('postId', '==', postId),
        orderBy('createdAt', 'asc')
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      const comments = commentsSnapshot.docs.map(commentDoc => ({
        id: commentDoc.id,
        ...commentDoc.data(),
        createdAt: commentDoc.data().createdAt?.toDate?.() || commentDoc.data().createdAt
      }));

      return {
        id: postDoc.id,
        ...postData,
        comments: postData.comments || comments.length, // Use stored count or calculate from comments array
        likes: postData.likes || 0, // Ensure likes field exists
        likedBy: postData.likedBy || [], // Ensure likedBy field exists
        createdAt: postData.createdAt?.toDate?.() || postData.createdAt,
        updatedAt: postData.updatedAt?.toDate?.() || postData.updatedAt,
        commentsArray: comments // Store actual comments array separately
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting post:', error);
    throw error;
  }
};

// Create a new post
export const createPost = async (postData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to create posts');
    }

    // Get user data using helper function
    const userData = await getUserDataForPost(auth.currentUser.uid);

    const postsRef = collection(db, COMMUNITY_POSTS_COLLECTION);

    // Lightweight duplicate protection: if an identical post was just created by this user
    // within the last few seconds, return that instead of writing a new one.
    try {
      const existingQuery = query(postsRef, where('authorId', '==', auth.currentUser.uid));
      const existingSnapshot = await getDocs(existingQuery);
      const now = Date.now();

      const recentDuplicateDoc = existingSnapshot.docs.find((d) => {
        const data = d.data();
        if (data.title !== postData.title || data.description !== postData.description) {
          return false;
        }
        const createdAt = data.createdAt?.toDate?.() || data.createdAt;
        if (!createdAt) return false;
        return now - new Date(createdAt).getTime() < 5000;
      });

      if (recentDuplicateDoc) {
        const existingData = recentDuplicateDoc.data();
        console.log('Skipping duplicate community post for user; returning existing document instead.', {
          postId: recentDuplicateDoc.id,
        });
        return {
          id: recentDuplicateDoc.id,
          ...existingData,
        };
      }
    } catch (duplicateCheckError) {
      console.warn('Error during community post duplicate check; proceeding with create.', duplicateCheckError);
    }
    const newPost = {
      title: postData.title,
      description: postData.description,
      type: postData.type,
      category: postData.category,
      author: userData.authorName,
      authorId: auth.currentUser.uid,
      authorAvatar: userData.authorAvatar,
      userType: userData.userType, // Store user type to distinguish between internal and client
      likes: 0,
      likedBy: [],
      comments: 0,
      files: postData.files || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    const docRef = await addDoc(postsRef, newPost);
    
    // Trigger community post notification
    try {
      const { triggerCommunityPostNotification } = await import('../notificationTriggerService');
      await triggerCommunityPostNotification({
        author: newPost.author,
        title: newPost.title,
        content: newPost.description,
        category: newPost.category,
        type: newPost.type,
        timestamp: new Date().toISOString()
      });
      console.log('✅ Community post notification sent successfully');
    } catch (notificationError) {
      console.error('❌ Error sending community post notification:', notificationError);
      // Don't fail the post creation if notification fails
    }
    
    return {
      id: docRef.id,
      ...newPost
    };
  } catch (error) {
    console.error('Error creating post:', error);
    throw error;
  }
};

// Update a post
export const updatePost = async (postId, updateData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update posts');
    }

    const postRef = doc(db, COMMUNITY_POSTS_COLLECTION, postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postDoc.data();
    if (postData.authorId !== auth.currentUser.uid) {
      throw new Error('You can only update your own posts');
    }

    await updateDoc(postRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });

    return postId;
  } catch (error) {
    console.error('Error updating post:', error);
    throw error;
  }
};

// Delete a post
export const deletePost = async (postId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete posts');
    }

    const postRef = doc(db, COMMUNITY_POSTS_COLLECTION, postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postDoc.data();
    if (postData.authorId !== auth.currentUser.uid) {
      throw new Error('You can only delete your own posts');
    }

    // Delete all comments for this post
    const commentsRef = collection(db, COMMUNITY_COMMENTS_COLLECTION);
    const commentsQuery = query(commentsRef, where('postId', '==', postId));
    const commentsSnapshot = await getDocs(commentsQuery);
    
    const deletePromises = commentsSnapshot.docs.map(commentDoc => 
      deleteDoc(doc(db, COMMUNITY_COMMENTS_COLLECTION, commentDoc.id))
    );
    await Promise.all(deletePromises);

    // Delete the post
    await deleteDoc(postRef);
    return postId;
  } catch (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
};

// Like/unlike a post
export const togglePostLike = async (postId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to like posts');
    }

    const postRef = doc(db, COMMUNITY_POSTS_COLLECTION, postId);
    const postDoc = await getDoc(postRef);
    
    if (!postDoc.exists()) {
      throw new Error('Post not found');
    }
    
    const postData = postDoc.data();
    const userId = auth.currentUser.uid;
    const isLiked = postData.likedBy?.includes(userId);

    if (isLiked) {
      // Unlike
      await updateDoc(postRef, {
        likes: postData.likes - 1,
        likedBy: arrayRemove(userId),
        updatedAt: serverTimestamp()
      });
    } else {
      // Like
      await updateDoc(postRef, {
        likes: postData.likes + 1,
        likedBy: arrayUnion(userId),
        updatedAt: serverTimestamp()
      });
    }

    return { postId, isLiked: !isLiked };
  } catch (error) {
    console.error('Error toggling post like:', error);
    throw error;
  }
};

// Add a comment to a post
export const addComment = async (postId, commentData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to add comments');
    }

    // Get user data using helper function
    const userData = await getUserDataForPost(auth.currentUser.uid);

    const commentsRef = collection(db, COMMUNITY_COMMENTS_COLLECTION);
    const newComment = {
      postId: postId,
      content: commentData.content,
      author: userData.authorName,
      authorId: auth.currentUser.uid,
      authorAvatar: userData.authorAvatar,
      userType: userData.userType, // Store user type to distinguish between internal and client
      parentCommentId: commentData.parentCommentId || null,
      likes: 0,
      likedBy: [],
      createdAt: serverTimestamp()
    };
    
    const docRef = await addDoc(commentsRef, newComment);

    // Update post comment count - increment for all comments
    const postRef = doc(db, COMMUNITY_POSTS_COLLECTION, postId);
    await updateDoc(postRef, {
      comments: increment(1), // Increment comment count for all comments
      updatedAt: serverTimestamp()
    });

    return {
      id: docRef.id,
      ...newComment
    };
  } catch (error) {
    console.error('Error adding comment:', error);
    throw error;
  }
};

// Update a comment
export const updateComment = async (commentId, updateData) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to update comments');
    }

    const commentRef = doc(db, COMMUNITY_COMMENTS_COLLECTION, commentId);
    const commentDoc = await getDoc(commentRef);
    
    if (!commentDoc.exists()) {
      throw new Error('Comment not found');
    }
    
    const commentData = commentDoc.data();
    if (commentData.authorId !== auth.currentUser.uid) {
      throw new Error('You can only update your own comments');
    }

    await updateDoc(commentRef, {
      content: updateData.content,
      updatedAt: serverTimestamp()
    });

    return commentId;
  } catch (error) {
    console.error('Error updating comment:', error);
    throw error;
  }
};

// Delete a comment
export const deleteComment = async (commentId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to delete comments');
    }

    const commentRef = doc(db, COMMUNITY_COMMENTS_COLLECTION, commentId);
    const commentDoc = await getDoc(commentRef);
    
    if (!commentDoc.exists()) {
      throw new Error('Comment not found');
    }
    
    const commentData = commentDoc.data();
    if (commentData.authorId !== auth.currentUser.uid) {
      throw new Error('You can only delete your own comments');
    }

    // Recursively delete all nested replies to this comment
    const deleteNestedReplies = async (parentCommentId) => {
      const repliesQuery = query(
        collection(db, COMMUNITY_COMMENTS_COLLECTION),
        where('parentCommentId', '==', parentCommentId)
      );
      const repliesSnapshot = await getDocs(repliesQuery);
      
      let totalDeleted = 0;
      for (const replyDoc of repliesSnapshot.docs) {
        // Recursively delete nested replies
        totalDeleted += await deleteNestedReplies(replyDoc.id);
        // Delete the reply itself
        await deleteDoc(doc(db, COMMUNITY_COMMENTS_COLLECTION, replyDoc.id));
        totalDeleted += 1;
      }
      
      return totalDeleted;
    };

    // Delete all nested replies and count them
    const totalRepliesDeleted = await deleteNestedReplies(commentId);

    // Delete the comment
    await deleteDoc(commentRef);

    // Update post comment count - decrement for the comment and all its nested replies
    const totalCommentsToRemove = 1 + totalRepliesDeleted; // 1 for the comment + all nested replies
    const postRef = doc(db, COMMUNITY_POSTS_COLLECTION, commentData.postId);
    await updateDoc(postRef, {
      comments: increment(-totalCommentsToRemove), // Decrement comment count for comment + all nested replies
      updatedAt: serverTimestamp()
    });

    return commentId;
  } catch (error) {
    console.error('Error deleting comment:', error);
    throw error;
  }
};

// Like/unlike a comment
export const toggleCommentLike = async (commentId) => {
  try {
    const auth = getAuth();
    if (!auth.currentUser) {
      throw new Error('User must be authenticated to like comments');
    }

    const commentRef = doc(db, COMMUNITY_COMMENTS_COLLECTION, commentId);
    const commentDoc = await getDoc(commentRef);
    
    if (!commentDoc.exists()) {
      throw new Error('Comment not found');
    }
    
    const commentData = commentDoc.data();
    const userId = auth.currentUser.uid;
    const isLiked = commentData.likedBy?.includes(userId);

    if (isLiked) {
      // Unlike
      await updateDoc(commentRef, {
        likes: commentData.likes - 1,
        likedBy: arrayRemove(userId),
        updatedAt: serverTimestamp()
      });
    } else {
      // Like
      await updateDoc(commentRef, {
        likes: commentData.likes + 1,
        likedBy: arrayUnion(userId),
        updatedAt: serverTimestamp()
      });
    }

    return { commentId, isLiked: !isLiked };
  } catch (error) {
    console.error('Error toggling comment like:', error);
    throw error;
  }
};

// Community members: same eligibility & portal linkage as CRM Messages (program tier + kickoff rules + team logins)
export const getActiveClients = async () => {
  try {
    return await buildCommunityMemberRows(await fetchCommunityDirectory());
  } catch (error) {
    console.error('Error getting community members:', error);
    throw error;
  }
};

// Community members for the page: the directory (with each company's logins) is loaded
// once. Other companies' users docs can't be watched from the portal (firestore.rules).
export const subscribeToMembers = (callback) => {
  let cancelled = false;
  getActiveClients()
    .then((members) => {
      if (!cancelled) callback(members);
    })
    .catch((error) => console.error('Error loading community members:', error));
  return () => {
    cancelled = true;
  };
};

// Get user by email
export const getUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return {
      id: userDoc.id,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error getting user by email:', error);
    throw error;
  }
};

// Update user active status for community
export const updateUserActiveStatus = async (userId, isActive) => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      isActive: isActive,
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error updating user active status:', error);
    throw error;
  }
};

// Search posts
export const searchPosts = async (searchQuery) => {
  try {
    const postsRef = collection(db, COMMUNITY_POSTS_COLLECTION);
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const allPosts = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      comments: doc.data().comments || 0, // Ensure comments field exists
      likes: doc.data().likes || 0, // Ensure likes field exists
      likedBy: doc.data().likedBy || [], // Ensure likedBy field exists
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
      updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt
    }));

    // Filter posts based on search query
    const query = searchQuery.toLowerCase().trim();
    return allPosts.filter(post => 
      post.title.toLowerCase().includes(query) ||
      post.description.toLowerCase().includes(query) ||
      post.author.toLowerCase().includes(query)
    );
  } catch (error) {
    console.error('Error searching posts:', error);
    throw error;
  }
};

// Real-time listener for posts
export const subscribeToPosts = (callback) => {
  const postsRef = collection(db, COMMUNITY_POSTS_COLLECTION);
  const q = query(postsRef, orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, async (snapshot) => {
    // Process posts and populate author data
    const posts = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const postData = doc.data();
        
        // Get current author data
        const userData = await getUserDataForPost(postData.authorId);
        
        return {
          id: doc.id,
          ...postData,
          author: userData.authorName,
          authorAvatar: userData.authorAvatar,
          userType: userData.userType,
          comments: postData.comments || 0,
          likes: postData.likes || 0,
          likedBy: postData.likedBy || [],
          createdAt: postData.createdAt?.toDate?.() || postData.createdAt,
          updatedAt: postData.updatedAt?.toDate?.() || postData.updatedAt
        };
      })
    );
    
    callback(posts);
  });
};

// Real-time listener for comments
export const subscribeToComments = (postId, callback) => {
  const commentsRef = collection(db, COMMUNITY_COMMENTS_COLLECTION);
  const q = query(
    commentsRef, 
    where('postId', '==', postId),
    orderBy('createdAt', 'asc')
  );
  
  return onSnapshot(q, (snapshot) => {
    const allComments = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
    }));
    
    // Organize comments into hierarchical structure with unlimited nesting
    const buildCommentTree = (comments, parentId = null) => {
      return comments
        .filter(comment => comment.parentCommentId === parentId)
        .map(comment => ({
          ...comment,
          replies: buildCommentTree(comments, comment.id)
        }))
        .sort((a, b) => a.createdAt - b.createdAt);
    };
    
    const hierarchicalComments = buildCommentTree(allComments);
    callback(hierarchicalComments);
  });
}; 